<script lang="ts">
const envVar = useRuntimeConfig();

export default {
  name: "Attachment",
  emits: ["update:modelValue"],
  props: {
    inputLabel: {
      type: String,
      default: null,
    },
    displayLabel: {
      type: String,
      default: null,
    },
    modelValue: {
      type: Object,
      default: null,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
    linkOnly: {
      type: Boolean,
      default: true,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: "outlined",
    },
    density: {
      type: String,
      default: "default",
    },
    filterAllowedTypes: {
      type: Boolean,
      default: true,
    },
    class: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      attachment: null as { id: string; fileName: string } | null,
      file: null as any,
      errorMessage: "",
    };
  },
  methods: {
    async addAttachment(file: any) {
      const allowedTypes = envVar.public.allowedTypes;

      if (file && file.length > 0) {
        const currentFile = file[0];
        this.errorMessage = "";

        // Check if the selected file type is allowed
        if (
          (this.filterAllowedTypes && !currentFile.type) ||
          !allowedTypes.includes(currentFile.type)
        ) {
          this.errorMessage = this.$t("attachment.errors.allowedTypes");
          nextTick(() => {
            this.$refs.fileUpload.reset();
            document.activeElement.blur(); //Remove focus
          });
          return;
        }

        await useProspect().addAttachment(
          currentFile,
          (attachmentId: string) => {
            this.attachment = {
              id: attachmentId,
              fileName: currentFile.name,
            };

            this.$emit("update:modelValue", this.attachment);
          },
          () => {
            this.errorMessage = this.$t("attachment.errors.unhandledError");
          }
        );
      }
    },
    deleteAttachment() {
      this.attachment = null;
      this.$emit("update:modelValue", this.attachment);
    },
  },
  watch: {
    modelValue: {
      async handler(newValue, oldValue) {
        this.attachment = newValue;
      },
    },
  },
  created() {
    this.attachment = this.modelValue;
  },
};
</script>

<template>
  <template v-if="!attachment">
    <v-file-input
      ref="fileUpload"
      v-model="file"
      @update:model-value="addAttachment"
      :label="inputLabel"
      :variant="variant"
      :hide-details="hideDetails"
      :density="density"
      autocomplete="off"
      aria-autocomplete="none"
      clearable
    ></v-file-input>
    <span v-if="errorMessage" class="error-message">
      {{ errorMessage }}
    </span>
  </template>
  <template v-else-if="linkOnly">
    <div class="message-attachments" :class="class">
      <div class="message-attachment">
        <v-icon color="rgba(0, 0, 0, 0.87)">mdi-paperclip</v-icon>
        <a
          class="ml-2"
          href="#"
          :title="attachment.fileName ?? $t('attachment.fileNoName')"
          @click.prevent.self="
            useProspect().downloadAttachment(
              attachment.id,
              attachment.fileName ?? $t('attachment.fileNoName')
            )
          "
        >
          {{ attachment.fileName ?? $t("attachment.fileNoName") }}
        </a>
      </div>
    </div>
  </template>
  <template v-else>
    <div
      :class="`v-input v-input--horizontal v-input--density-${density} v-file-input`"
    >
      <div class="v-input__prepend">
        <i
          class="mdi-paperclip mdi v-icon notranslate v-theme--light v-icon--size-default v-icon--clickable"
          role="button"
          aria-hidden="false"
        ></i>
      </div>
      <div class="v-input__control">
        <div
          :class="`v-field v-field--active v-field--appended v-field--variant-${variant} v-theme--light`"
        >
          <div class="v-field__field cim-display-link">
            <div class="v-field__input">
              <div class="v-field__input_link">
                <a
                  href="#"
                  @click.prevent.self="
                    useProspect().downloadAttachment(
                      attachment.id,
                      attachment.fileName
                    )
                  "
                  >{{ attachment.fileName }}</a
                >
              </div>
            </div>
          </div>
          <div class="v-field__clearable">
            <i
              v-if="canDelete"
              class="mdi-close-circle mdi v-icon notranslate v-theme--light v-icon--size-default v-icon--clickable"
              role="button"
              aria-hidden="false"
              @click="deleteAttachment()"
              :aria-label="`Clear ${displayLabel}`"
            ></i>
          </div>
          <div class="v-field__outline">
            <div class="v-field__outline__start"></div>
            <div class="v-field__outline__notch">
              <label
                v-if="displayLabel"
                class="v-label v-field-label v-field-label--floating"
                aria-hidden="false"
                >{{ displayLabel }}</label
              >
            </div>
            <div class="v-field__outline__end"></div>
          </div>
        </div>
      </div>
      <div v-if="!hideDetails" class="v-input__details">
        <div class="v-messages" role="alert"></div>
        <!---->
      </div>
    </div>
  </template>
</template>

<style scoped lang="scss">
.error-message {
  color: rgb(176, 0, 32);
  font-size: 12px;
  line-height: 12px;
  padding-left: 44px;
}

.message-attachment {
  margin-top: 8px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  a {
    &:link,
    &:visited,
    &:active {
      color: #E05C91;
      text-decoration: none;
    }

    &:hover {
      text-decoration: underline;
    }
  }
}

.cim-display-link {
  width: auto;
  text-align: left;
  vertical-align: middle;

  .v-field__input_link {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  a {
    color: #E05C91;
    font-weight: bold;
    white-space: nowrap;

    &:link {
      color: #E05C91;
    }
    &:hover,
    &:active {
      color: #a74271;
    }
  }
}
</style>
